import { useState, useId, type FormEvent } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/cn'

export type NewsletterSource = 'hero' | 'footer' | 'modal'

interface Props {
  source: NewsletterSource
  className?: string
  variant?: 'light' | 'dark'
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

export function NewsletterSignup({
  source,
  className,
  variant = 'light',
}: Props) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [honeypot, setHoneypot] = useState('')
  const inputId = useId()

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) {
      setStatus('error')
      setErrorMessage('Please enter your email.')
      return
    }
    setStatus('submitting')
    setErrorMessage(null)

    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, source, website: honeypot }),
      })
      if (!res.ok) {
        setStatus('error')
        setErrorMessage(
          res.status === 400
            ? 'Please enter a valid email address.'
            : res.status === 429
              ? 'Too many attempts. Please try again in a few minutes.'
              : 'Sign-up failed. Please try again in a moment.',
        )
        return
      }
    } catch {
      setStatus('error')
      setErrorMessage('Network issue. Please check your connection and try again.')
      return
    }
    setStatus('success')
    setEmail('')
  }

  // Underlined inline form. No pill-shaped CTA. No card chrome.
  const isDark = variant === 'dark'
  const inputCls = cn(
    'flex-1 min-w-0 border-0 border-b bg-transparent px-0 py-2.5 text-sm focus-visible:outline-none transition-colors duration-200 ease-out-quart',
    isDark
      ? 'border-surface/30 text-surface placeholder:text-surface/55 focus-visible:border-surface'
      : 'border-ink-deep/30 text-ink-deep placeholder:text-ink-faint focus-visible:border-brand',
  )
  const buttonCls = cn(
    'inline-flex h-10 w-10 shrink-0 items-center justify-center transition-colors duration-200 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    isDark
      ? 'text-surface hover:bg-surface hover:text-ink-deep focus-visible:ring-surface focus-visible:ring-offset-ink-deep'
      : 'text-ink-deep hover:bg-ink-deep hover:text-surface focus-visible:ring-brand focus-visible:ring-offset-surface',
  )
  const labelCls = cn(
    'eyebrow',
    isDark ? 'text-surface/55' : 'text-ink-muted',
  )
  const helpCls = cn(
    'text-xs',
    isDark ? 'text-surface/60' : 'text-ink-faint',
  )

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('w-full', className)}
      noValidate
      aria-describedby={`${inputId}-help`}
    >
      <label htmlFor={inputId} className={labelCls}>
        Email
      </label>
      <div className="mt-2 flex items-baseline gap-3">
        <input
          id={inputId}
          type="email"
          name="email"
          autoComplete="email"
          required
          placeholder="you@organization.org"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={status === 'submitting'}
          className={inputCls}
        />
        <button
          type="submit"
          disabled={status === 'submitting'}
          aria-label="Subscribe"
          className={buttonCls}
        >
          <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {/* Honeypot: hidden from people and assistive tech; bots fill it. */}
      <div className="hidden" aria-hidden>
        <label htmlFor={`${inputId}-website`}>Website</label>
        <input
          id={`${inputId}-website`}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(event) => setHoneypot(event.target.value)}
        />
      </div>
      <p
        id={`${inputId}-help`}
        aria-live="polite"
        className={cn('mt-3', helpCls)}
      >
        {status === 'success' && 'Thanks. Please check your inbox.'}
        {status === 'error' && (errorMessage ?? 'Sign-up failed. Try again.')}
        {status === 'idle' && 'Roughly monthly. Unsubscribe any time.'}
        {status === 'submitting' && 'Sending…'}
      </p>
    </form>
  )
}
